package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.FeedbackType
import fr.manyverse.app.domain.validation.FeedbackValidator
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.FeedbackViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.feedback_bug_prompt
import manyverse.shared.generated.resources.feedback_bug_title
import manyverse.shared.generated.resources.feedback_discord_button
import manyverse.shared.generated.resources.feedback_header
import manyverse.shared.generated.resources.feedback_message_label
import manyverse.shared.generated.resources.feedback_send
import manyverse.shared.generated.resources.feedback_suggestion_prompt
import manyverse.shared.generated.resources.feedback_suggestion_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

private const val DISCORD_URL = "https://discord.gg/fUBK2r6wd4"
private val DiscordBlurple = Color(0xFF5865F2)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FeedbackScreen(
    type: FeedbackType,
    userId: String,
    userEmail: String,
    onNavigateBack: () -> Unit,
    onSubmitted: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: FeedbackViewModel = koinViewModel()
) {
    var message by remember { mutableStateOf("") }
    val submitState by viewModel.submitState.collectAsStateWithLifecycle()
    val uriHandler = LocalUriHandler.current

    val titleRes = when (type) {
        FeedbackType.BUG -> Res.string.feedback_bug_title
        FeedbackType.SUGGESTION -> Res.string.feedback_suggestion_title
    }
    val promptRes = when (type) {
        FeedbackType.BUG -> Res.string.feedback_bug_prompt
        FeedbackType.SUGGESTION -> Res.string.feedback_suggestion_prompt
    }

    LaunchedEffect(submitState) {
        if (submitState is AsyncUiState.Success) {
            viewModel.reset()
            onSubmitted()
        }
    }

    val isLoading = submitState is AsyncUiState.Loading

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(titleRes)) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        modifier = modifier
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .imePadding()
                .verticalScroll(rememberScrollState())
                .padding(16.dp)
                .safeContentPadding(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(
                text = stringResource(Res.string.feedback_header),
                style = MaterialTheme.typography.bodyMedium
            )

            Button(
                onClick = { uriHandler.openUri(DISCORD_URL) },
                colors = ButtonDefaults.buttonColors(
                    containerColor = DiscordBlurple,
                    contentColor = Color.White
                ),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(stringResource(Res.string.feedback_discord_button))
            }

            Text(
                text = stringResource(promptRes),
                style = MaterialTheme.typography.bodyMedium
            )

            OutlinedTextField(
                value = message,
                onValueChange = {
                    if (it.length <= FeedbackValidator.MAX_FEEDBACK_LENGTH) {
                        message = it
                    }
                },
                label = { Text(stringResource(Res.string.feedback_message_label)) },
                minLines = 5,
                maxLines = 12,
                enabled = !isLoading,
                supportingText = {
                    Text("${message.length}/${FeedbackValidator.MAX_FEEDBACK_LENGTH}")
                },
                keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag("feedbackMessageField")
            )

            if (submitState is AsyncUiState.Error) {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.errorContainer
                    ),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = (submitState as AsyncUiState.Error).message,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.padding(16.dp)
                    )
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                OutlinedButton(
                    onClick = onNavigateBack,
                    enabled = !isLoading,
                    modifier = Modifier.weight(1f)
                ) {
                    Text(stringResource(Res.string.cancel))
                }

                Button(
                    onClick = { viewModel.submit(userId, userEmail, type, message) },
                    enabled = message.isNotBlank() && !isLoading,
                    modifier = Modifier
                        .weight(1f)
                        .testTag("feedbackSendButton")
                ) {
                    if (isLoading) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(24.dp),
                            color = MaterialTheme.colorScheme.onPrimary,
                            strokeWidth = 2.dp
                        )
                    } else {
                        Text(stringResource(Res.string.feedback_send))
                    }
                }
            }
        }
    }
}
